import { z } from "zod"


export type LanguageCode = string

export type LocalizedStringEntry<TExtra extends Record<string, unknown> = Record<string, unknown>> = {
  value: string
  updatedAt: Date
  autoTranslated?: boolean
} & TExtra

export type LocalizedString<TExtra extends Record<string, unknown> = Record<string, unknown>> = Record<
  LanguageCode,
  LocalizedStringEntry<TExtra>
>

export type I18nStringRecord = LocalizedString

export type I18nString = LocalizedString

export type LocalizedStringWithFallback = LocalizedString & {
  get: (key: string) => LocalizedStringEntry | undefined
  getExact: (key: string) => LocalizedStringEntry | undefined
  hasExact: (key: string) => boolean
}

export const LANGUAGE_CODE_REGEX = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/

const LOCALIZED_STRING_PROXY_CACHE = new WeakMap<object, unknown>()

function normalizeLocale(locale: string): string {
  const trimmed = locale.trim()
  if (!trimmed) return ""
  const getCanonicalLocales = (Intl as typeof Intl & {
    getCanonicalLocales?: (locales: string | readonly string[]) => string[]
  }).getCanonicalLocales
  if (typeof getCanonicalLocales !== "function") return trimmed
  try {
    return getCanonicalLocales(trimmed)[0] ?? trimmed
  } catch {
    return trimmed
  }
}

export function buildLocaleFallbackChain(locale: string, fallbacks?: string | string[]): string[] {
  const base = locale.trim()
  const canonical = normalizeLocale(base)
  const extra = typeof fallbacks === "string" ? [fallbacks] : (fallbacks ?? [])

  const output: string[] = []
  const seen = new Set<string>()

  const push = (value: string) => {
    if (!value) return
    if (seen.has(value)) return
    seen.add(value)
    output.push(value)
  }

  const addChain = (value: string) => {
    if (!value) return
    const parts = value.split("-").filter(Boolean)
    while (parts.length > 0) {
      push(parts.join("-"))
      parts.pop()
    }
  }

  addChain(base)
  addChain(canonical)
  for (const fallback of extra) addChain(normalizeLocale(fallback))

  return output
}

export function resolveLocalizedString(
  value: LocalizedString | null | undefined,
  locale: string,
  options?: { fallbacks?: string | string[] }
): string | undefined {
  return resolveLocalizedStringEntry(value, locale, options)?.value
}

function resolveLocalizedStringEntry(
  value: LocalizedString | null | undefined,
  locale: string,
  options?: { fallbacks?: string | string[] }
): LocalizedStringEntry | undefined {
  if (!value) return undefined
  const chain = buildLocaleFallbackChain(locale, options?.fallbacks)
  if (chain.length === 0) return undefined

  const record = value
  for (const key of chain) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue
    return record[key] as LocalizedStringEntry
  }
  return undefined
}

export function withLocalizedStringFallback<T>(value: T): T {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value
  if (value instanceof Map) return value
  const cached = LOCALIZED_STRING_PROXY_CACHE.get(value)
  if (cached) return cached as T

  const proxy = withFallbackRecord(value as Record<string, unknown>)
  LOCALIZED_STRING_PROXY_CACHE.set(value, proxy)
  return proxy as T
}

function withFallbackRecord(record: Record<string, unknown>): LocalizedStringWithFallback {
  const getExact = (key: string): LocalizedStringEntry | undefined => {
    if (!hasExact(key)) return undefined
    return record[key] as LocalizedStringEntry
  }
  const hasExact = (key: string) => Object.prototype.hasOwnProperty.call(record, key)

  return new Proxy(record, {
    get(target, prop) {
      if (prop === "getExact") return getExact
      if (prop === "hasExact") return hasExact
      if (prop === "get") return (key: string) => resolveLocalizedStringEntry(target as LocalizedString, key)
      if (typeof prop === "string" && !(prop in target)) {
        return resolveLocalizedStringEntry(target as LocalizedString, prop)
      }

      return Reflect.get(target, prop)
    },
    set(target, prop, next) {
      return Reflect.set(target, prop, next)
    },
  }) as LocalizedStringWithFallback
}

export const zLocalizedString = () => {
  const languageCodeSchema = z
    .string()
    .regex(LANGUAGE_CODE_REGEX, { message: "Expected a language code (BCP 47, e.g. en or fr-FR)." })
  const entrySchema = z.object({
    value: z.string(),
    updatedAt: z.date(),
    autoTranslated: z.boolean().optional(),
  }).passthrough()
  const schema = z.record(
    languageCodeSchema,
    entrySchema,
  )

  return schema
}

export const zI18nString = zLocalizedString
